import React from 'react';
import { useAppDispatch, useAppSelector } from '../../store';
import {
  dismissOnboardingWelcome,
  markFirstRunCompleted,
  openOnboardingChecklist,
  startOnboardingTour,
} from '../../store/slices/helpSlice';
import useAuth from '../../hooks/useAuth';
import {
  hasSeenOnboardingWelcome,
  markOnboardingWelcomeSeen,
} from '../../lib/help/helpPersistence';
import { trackHelpEvent } from '../../lib/help/helpAnalytics';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from '../ui/Dialog';
import { Button } from '../ui/Button';
import { useTranslation } from 'react-i18next';

const OnboardingWelcomeModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { user, loading } = useAuth();
  const dismissed = useAppSelector((state) => state.help.onboardingWelcomeDismissed);
  // Only consider showing once auth has resolved and a real user is present.
  // Previously the modal could render while `user` was still null, so "Skip
  // for now" wrote the seen-flag under the `anonymous` key; after the next
  // navigation (full reload) the resolved user.uid key did not match and the
  // welcome modal re-prompted on every visit (and overlaid the Alerts panel).
  const open = !loading && !!user && !dismissed && !hasSeenOnboardingWelcome(user);

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          markOnboardingWelcomeSeen(user);
          dispatch(dismissOnboardingWelcome());
        }
      }}
    >
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>{t('help.welcomeTitle', { defaultValue: 'Welcome to the app' })}</DialogTitle>
          <DialogDescription>
            {t('help.welcomeDescription', {
              defaultValue:
                'Get started with a short guided setup so you can reach useful results quickly.',
            })}
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <ol className="list-decimal space-y-2 pl-5 text-sm text-text-secondary">
            <li>{t('help.welcomeStep1', { defaultValue: 'Set filters for your strategy.' })}</li>
            <li>{t('help.welcomeStep2', { defaultValue: 'Run your first screen.' })}</li>
            <li>
              {t('help.welcomeStep3', {
                defaultValue: 'Open chart + deep analysis for one candidate.',
              })}
            </li>
            <li>
              {t('help.welcomeStep4', {
                defaultValue: 'Save favorites and create an alert rule.',
              })}
            </li>
          </ol>
        </DialogBody>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              markOnboardingWelcomeSeen(user);
              dispatch(markFirstRunCompleted());
              dispatch(dismissOnboardingWelcome());
              trackHelpEvent('onboarding_skipped', { source: 'welcome_modal' });
            }}
          >
            {t('help.skipForNow', { defaultValue: 'Skip for now' })}
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              markOnboardingWelcomeSeen(user);
              dispatch(dismissOnboardingWelcome());
              dispatch(openOnboardingChecklist());
              dispatch(startOnboardingTour());
              trackHelpEvent('onboarding_started', { source: 'welcome_modal' });
            }}
          >
            {t('help.startOnboarding', { defaultValue: 'Start onboarding' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingWelcomeModal;
